import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

// Admin phone numbers
const ADMIN_PHONE_NUMBERS = [
  '+15407939110',
];

function Carpools() {
  const [carpools, setCarpools] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCarpool, setEditingCarpool] = useState(null);
  const [editingDeparture, setEditingDeparture] = useState({});
  const [viewAsNonAdmin, setViewAsNonAdmin] = useState(false);
  const [newCarpool, setNewCarpool] = useState({
    title: '',
    driverUserId: '',
    driver: '',
    maxSpots: 4,
    departureDay: '',
    departureTime: ''
  });
  const { currentUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isActualAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);
  const isAdmin = isActualAdmin && !viewAsNonAdmin;
  
  // Get the previous route from location state, default to /home if not specified
  const getBackRoute = () => {
    if (isActualAdmin && !viewAsNonAdmin) return '/admin';
    return location.state?.from || '/home';
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const checkConfirmedStatusAndLoad = async () => {
      // Admins can always access
      if (!isActualAdmin) {
        try {
          // Check Firestore for user's response
          if (db && currentUser?.uid) {
            const responsesRef = collection(db, 'responses');
            const q = query(responsesRef, where('userId', '==', currentUser.uid));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const userResponse = querySnapshot.docs[0].data();
              const confirmed = userResponse.status === 'confirmed';
              
              if (!confirmed) {
                // Redirect if not confirmed
                navigate('/home', { 
                  state: { 
                    message: 'You must be confirmed for the trip to access carpools. Please contact an admin.' 
                  } 
                });
                return;
              }
            } else {
              // No response found, check localStorage
              const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
              const userResponse = localResponses.find(r => r.userId === currentUser.uid);
              
              if (!userResponse || userResponse.status !== 'confirmed') {
                navigate('/home', { 
                  state: { 
                    message: 'You must be confirmed for the trip to access carpools. Please contact an admin.' 
                  } 
                });
                return;
              }
            }
          } else {
            // No user ID or database, check localStorage
            const isDevMode = localStorage.getItem('devMode') === 'true';
            if (!isDevMode) {
              navigate('/home', { 
                state: { 
                  message: 'You must be confirmed for the trip to access carpools. Please contact an admin.' 
                } 
              });
              return;
            }
          }
        } catch (error) {
          console.error('Error checking confirmed status:', error);
          // Fallback: check localStorage
          const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
          const userResponse = localResponses.find(r => r.userId === currentUser?.uid);
          
          if (!userResponse || userResponse.status !== 'confirmed') {
            navigate('/home', { 
              state: { 
                message: 'You must be confirmed for the trip to access carpools. Please contact an admin.' 
              } 
            });
            return;
          }
        }
      }

      // If we get here, user is confirmed (or admin), so load data
      const loadData = async () => {
      try {
        // Load carpools
        const carpoolsQuery = query(collection(db, 'carpools'), orderBy('createdAt', 'desc'));
        const carpoolsUnsubscribe = onSnapshot(
          carpoolsQuery,
          (snapshot) => {
            const carpoolsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setCarpools(carpoolsData);
            setLoading(false);
            setError('');
          },
          (err) => {
            console.warn('Error loading carpools:', err);
            setError('Failed to load carpools. Please refresh the page.');
            setLoading(false);
          }
        );

        // Load responses to get user names
        const responsesQuery = query(collection(db, 'responses'));
        const responsesUnsubscribe = onSnapshot(
          responsesQuery,
          (snapshot) => {
            const responsesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setResponses(responsesData);
          },
          (err) => {
            console.warn('Error loading responses:', err);
            // Don't show error for responses, just log it
          }
        );

        return () => {
          carpoolsUnsubscribe();
          responsesUnsubscribe();
        };
      } catch (error) {
        console.error('Error setting up listeners:', error);
        setLoading(false);
      }
      };

      loadData();
    };

    checkConfirmedStatusAndLoad();
  }, [currentUser, navigate, isActualAdmin]);

  const getUserName = (userId) => {
    const response = responses.find(r => r.userId === userId);
    return response?.userName || 'Unknown';
  };

  const getUserPhone = (userId) => {
    const response = responses.find(r => r.userId === userId);
    return response?.phoneNumber || 'Unknown';
  };

  // Detect if user is on iOS or Mac Safari (both use iOS SMS format)
  const isIOS = () => {
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isMacSafari = /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua) && !/Firefox/.test(ua);
    return isIOSDevice || isMacSafari;
  };

  const isAndroid = () => {
    return /Android/.test(navigator.userAgent);
  };

  // Format phone number for SMS URL based on platform
  const formatPhoneForSMS = (phone) => {
    // Remove all non-digit characters for processing
    const cleaned = phone.replace(/\D/g, '');
    
    if (isIOS()) {
      // iOS format: 1-408-555-1212 (with dashes, no +)
      if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // Has country code: +14085551212 -> 1-408-555-1212
        return `1-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      } else if (cleaned.length === 10) {
        // No country code: 4085551212 -> 1-408-555-1212
        return `1-${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      // Fallback: try to format as best we can
      return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4');
    } else {
      // Android format: +12223334444 (with +, no dashes)
      if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `+${cleaned}`;
      } else if (cleaned.length === 10) {
        return `+1${cleaned}`;
      }
      // Fallback: ensure it starts with +
      return phone.startsWith('+') ? phone : `+${cleaned}`;
    }
  };

  // Create SMS group chat URL (excludes driver)
  const createGroupChatURL = (carpool) => {
    const passengers = carpool.passengers || [];
    const driverUserId = carpool.driverUserId;
    
    // Get passenger phone numbers (excluding driver)
    const passengerPhones = passengers
      .filter(userId => userId !== driverUserId)
      .map(userId => getUserPhone(userId))
      .filter(phone => phone && phone !== 'Unknown')
      .map(phone => formatPhoneForSMS(phone));

    if (passengerPhones.length === 0) {
      return null; // No passengers to message
    }

    const carpoolName = carpool.title || 'Carpool';
    const body = encodeURIComponent(carpoolName);

    if (isIOS()) {
      // iOS format: sms:/open?addresses=1-408-555-1212,1-408-555-2121&body=My sms text
      const addresses = passengerPhones.join(',');
      return `sms:/open?addresses=${addresses}&body=${body}`;
    } else if (isAndroid()) {
      // Android format: sms:+12223334444,+15556667777?&body=Hello%20world
      const addresses = passengerPhones.join(',');
      return `sms:${addresses}?&body=${body}`;
    } else {
      // Fallback for other platforms - use Android format
      const addresses = passengerPhones.join(',');
      return `sms:${addresses}?&body=${body}`;
    }
  };

  // Create SMS group chat URL for admin (includes all passengers including driver)
  const createGroupChatURLForAdmin = (carpool) => {
    const passengers = carpool.passengers || [];
    
    // Get all passenger phone numbers including driver
    const allPhones = passengers
      .map(userId => getUserPhone(userId))
      .filter(phone => phone && phone !== 'Unknown')
      .map(phone => formatPhoneForSMS(phone));

    if (allPhones.length === 0) {
      return null; // No passengers to message
    }

    const carpoolName = carpool.title || 'Carpool';
    const body = encodeURIComponent(carpoolName);

    if (isIOS()) {
      // iOS format: sms:/open?addresses=1-408-555-1212,1-408-555-2121&body=My sms text
      const addresses = allPhones.join(',');
      return `sms:/open?addresses=${addresses}&body=${body}`;
    } else if (isAndroid()) {
      // Android format: sms:+12223334444,+15556667777?&body=Hello%20world
      const addresses = allPhones.join(',');
      return `sms:${addresses}?&body=${body}`;
    } else {
      // Fallback for other platforms - use Android format
      const addresses = allPhones.join(',');
      return `sms:${addresses}?&body=${body}`;
    }
  };

  const handleCreateGroupChat = (carpool) => {
    const url = createGroupChatURL(carpool);
    if (!url) {
      setError('No passengers to message in this carpool.');
      return;
    }
    window.location.href = url;
  };

  const handleCreateGroupChatForAdmin = (carpool) => {
    const url = createGroupChatURLForAdmin(carpool);
    if (!url) {
      setError('No passengers to message in this carpool.');
      return;
    }
    window.location.href = url;
  };

  const handleCreateCarpool = async () => {
    if (!newCarpool.title.trim() || !newCarpool.driverUserId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const driverResponse = responses.find(r => r.userId === newCarpool.driverUserId);
      const driverName = driverResponse?.userName || 'Unknown';

      const carpoolData = {
        title: newCarpool.title.trim(),
        driver: driverName,
        driverUserId: newCarpool.driverUserId,
        maxSpots: parseInt(newCarpool.maxSpots) || 4,
        departureDay: newCarpool.departureDay || '',
        departureTime: newCarpool.departureTime || '',
        passengers: [newCarpool.driverUserId], // Automatically add driver to passengers
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'carpools'), carpoolData);
      setShowCreateModal(false);
      setNewCarpool({ title: '', driverUserId: '', driver: '', maxSpots: 4, departureDay: '', departureTime: '' });
      setError('');
    } catch (error) {
      console.error('Error creating carpool:', error);
      setError('Failed to create carpool. Please try again.');
    }
  };

  const handleUpdateCarpool = async () => {
    if (!editingCarpool.title.trim() || !editingCarpool.driverUserId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const driverResponse = responses.find(r => r.userId === editingCarpool.driverUserId);
      const driverName = driverResponse?.userName || 'Unknown';

      const carpoolData = {
        title: editingCarpool.title.trim(),
        driver: driverName,
        driverUserId: editingCarpool.driverUserId,
        maxSpots: parseInt(editingCarpool.maxSpots) || 4,
        departureDay: editingCarpool.departureDay || '',
        departureTime: editingCarpool.departureTime || '',
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'carpools', editingCarpool.id), carpoolData);
      setEditingCarpool(null);
      setError('');
    } catch (error) {
      console.error('Error updating carpool:', error);
      setError('Failed to update carpool. Please try again.');
    }
  };

  const handleUpdateDeparture = async (carpoolId, departureDay, departureTime) => {
    try {
      await updateDoc(doc(db, 'carpools', carpoolId), {
        departureDay: departureDay || '',
        departureTime: departureTime || '',
        updatedAt: new Date().toISOString()
      });
      setError('');
    } catch (error) {
      console.error('Error updating departure:', error);
      setError('Failed to update departure info. Please try again.');
    }
  };

  const handleDeleteCarpool = async (carpoolId) => {
    if (!window.confirm('Are you sure you want to delete this carpool? All passengers will be removed.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'carpools', carpoolId));
    } catch (error) {
      console.error('Error deleting carpool:', error);
      setError('Failed to delete carpool. Please try again.');
    }
  };

  const handleJoinCarpool = async (carpoolId) => {
    if (!currentUser) return;

    try {
      // Check if user is waitlisted - they can't join carpools
      const userResponse = responses.find(r => r.userId === currentUser.uid);
      if (userResponse && (userResponse.status === 'waitlist' || userResponse.isWaitlist === true)) {
        setError('You must be confirmed for the trip before joining a carpool. Please contact an admin.');
        return;
      }

      const carpool = carpools.find(c => c.id === carpoolId);
      if (!carpool) return;

      const currentPassengers = carpool.passengers || [];
      
      // Check if already in carpool
      if (currentPassengers.includes(currentUser.uid)) {
        setError('You are already in this carpool');
        return;
      }

      // Check if carpool is full
      const availableSpots = carpool.maxSpots - currentPassengers.length;
      if (availableSpots <= 0) {
        setError('This carpool is full');
        return;
      }

      // Remove from other carpools first
      const otherCarpools = carpools.filter(c => 
        c.id !== carpoolId && (c.passengers || []).includes(currentUser.uid)
      );
      
      for (const otherCarpool of otherCarpools) {
        const updatedPassengers = (otherCarpool.passengers || []).filter(uid => uid !== currentUser.uid);
        await updateDoc(doc(db, 'carpools', otherCarpool.id), {
          passengers: updatedPassengers,
          updatedAt: new Date().toISOString()
        });
      }

      // Add to selected carpool
      await updateDoc(doc(db, 'carpools', carpoolId), {
        passengers: [...currentPassengers, currentUser.uid],
        updatedAt: new Date().toISOString()
      });

      setError('');
    } catch (error) {
      console.error('Error joining carpool:', error);
      setError('Failed to join carpool. Please try again.');
    }
  };

  const handleLeaveCarpool = async (carpoolId) => {
    if (!currentUser) return;

    try {
      const carpool = carpools.find(c => c.id === carpoolId);
      if (!carpool) return;

      const currentPassengers = carpool.passengers || [];
      const updatedPassengers = currentPassengers.filter(uid => uid !== currentUser.uid);

      await updateDoc(doc(db, 'carpools', carpoolId), {
        passengers: updatedPassengers,
        updatedAt: new Date().toISOString()
      });

      setError('');
    } catch (error) {
      console.error('Error leaving carpool:', error);
      setError('Failed to leave carpool. Please try again.');
    }
  };

  const handleMovePassenger = async (fromCarpoolId, toCarpoolId, userId) => {
    try {
      const fromCarpool = carpools.find(c => c.id === fromCarpoolId);
      const toCarpool = carpools.find(c => c.id === toCarpoolId);

      if (!fromCarpool || !toCarpool) return;

      // Check if destination carpool has space
      const toPassengers = toCarpool.passengers || [];
      if (toPassengers.length >= toCarpool.maxSpots) {
        setError('Destination carpool is full');
        return;
      }

      // Remove from source carpool
      const fromPassengers = (fromCarpool.passengers || []).filter(uid => uid !== userId);
      await updateDoc(doc(db, 'carpools', fromCarpoolId), {
        passengers: fromPassengers,
        updatedAt: new Date().toISOString()
      });

      // Add to destination carpool
      await updateDoc(doc(db, 'carpools', toCarpoolId), {
        passengers: [...toPassengers, userId],
        updatedAt: new Date().toISOString()
      });

      setError('');
    } catch (error) {
      console.error('Error moving passenger:', error);
      setError('Failed to move passenger. Please try again.');
    }
  };

  const handleRemovePassenger = async (carpoolId, userId) => {
    if (!window.confirm('Remove this passenger from the carpool?')) {
      return;
    }

    try {
      const carpool = carpools.find(c => c.id === carpoolId);
      if (!carpool) return;

      const updatedPassengers = (carpool.passengers || []).filter(uid => uid !== userId);

      await updateDoc(doc(db, 'carpools', carpoolId), {
        passengers: updatedPassengers,
        updatedAt: new Date().toISOString()
      });

      setError('');
    } catch (error) {
      console.error('Error removing passenger:', error);
      setError('Failed to remove passenger. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading carpools...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h1 className="card-title">🚗 Carpools</h1>
              <p className="card-subtitle">Manage transportation for the trip</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {isActualAdmin && (
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  background: viewAsNonAdmin ? '#e2e8f0' : 'transparent',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)'
                }}>
                  <input
                    type="checkbox"
                    checked={viewAsNonAdmin}
                    onChange={(e) => setViewAsNonAdmin(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  View as User
                </label>
              )}
              <button
                onClick={() => {
                  // Auto-set current user as driver when opening modal
                  if (currentUser) {
                    const currentUserResponse = responses.find(r => r.userId === currentUser.uid);
                    setNewCarpool({
                      title: '',
                      driverUserId: currentUser.uid,
                      driver: currentUserResponse?.userName || 'Unknown',
                      maxSpots: 4,
                      departureDay: '',
                      departureTime: ''
                    });
                  }
                  setShowCreateModal(true);
                }}
                className="btn btn-primary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px'
                }}
              >
                + Create Carpool
              </button>
              <button
                onClick={() => navigate(getBackRoute())}
                className="btn btn-secondary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px'
                }}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {carpools.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: 'var(--text-tertiary)',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '10px' }}>No carpools yet</p>
            <p style={{ fontSize: '0.9rem' }}>
              {isAdmin ? 'Create a carpool to get started!' : 'Check back later for carpool options.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
            {carpools.map((carpool) => {
              const passengers = carpool.passengers || [];
              const availableSpots = carpool.maxSpots - passengers.length;
              const isUserInCarpool = currentUser && passengers.includes(currentUser.uid);
              const isUserDriver = carpool.driverUserId === currentUser?.uid;
              const canEditDeparture = isAdmin || isUserDriver;

              return (
                <div
                  key={carpool.id}
                  style={{
                    background: 'var(--bg-primary)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid var(--border-color)',
                    boxShadow: '0 2px 4px var(--shadow)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)', fontSize: '1.3rem' }}>
                        {carpool.title}
                      </h3>
                      <p style={{ margin: '0 0 4px 0', color: 'var(--text-secondary)', fontSize: '1rem' }}>
                        <strong>Driver:</strong> {carpool.driver}
                      </p>
                      {(carpool.departureDay || carpool.departureTime) && (
                        <p style={{ margin: '0 0 4px 0', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                          <strong>Departure:</strong> {carpool.departureDay || 'Not set'} {carpool.departureTime && `at ${carpool.departureTime}`}
                        </p>
                      )}
                      <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                        {passengers.length} / {carpool.maxSpots} spots filled
                        {availableSpots > 0 && (
                          <span style={{ color: '#2f855a', fontWeight: '600', marginLeft: '8px' }}>
                            ({availableSpots} available)
                          </span>
                        )}
                        {availableSpots === 0 && (
                          <span style={{ color: '#c53030', fontWeight: '600', marginLeft: '8px' }}>
                            (Full)
                          </span>
                        )}
                      </p>
                    </div>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setEditingCarpool({ ...carpool })}
                          className="btn btn-secondary"
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '0.85rem',
                            borderRadius: '6px'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCarpool(carpool.id)}
                          className="btn btn-secondary"
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '0.85rem',
                            borderRadius: '6px',
                            background: '#fed7d7',
                            color: '#c53030',
                            border: '1px solid #feb2b2'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: '15px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                      Passengers:
                    </h4>
                    {passengers.length === 0 ? (
                      <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '0.9rem' }}>
                        No passengers yet
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {passengers.map((userId) => {
                          const userName = getUserName(userId);
                          const userPhone = getUserPhone(userId);
                          const isCurrentUser = userId === currentUser?.uid;

                          return (
                            <div
                              key={userId}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 12px',
                                background: isCurrentUser 
                                  ? (isDark ? '#0f766e' : '#e6fffa') 
                                  : 'var(--bg-secondary)',
                                borderRadius: '8px',
                                border: isCurrentUser 
                                  ? (isDark ? '1px solid #14b8a6' : '1px solid #81e6d9') 
                                  : '1px solid var(--border-color)'
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: isCurrentUser ? '600' : '400', color: 'var(--text-primary)' }}>
                                  {userName} {isCurrentUser && '(You)'}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
                                  {userPhone}
                                </div>
                              </div>
                              {isAdmin && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {carpools.filter(c => c.id !== carpool.id && (c.passengers?.length || 0) < c.maxSpots).map((targetCarpool) => (
                                    <button
                                      key={targetCarpool.id}
                                      onClick={() => handleMovePassenger(carpool.id, targetCarpool.id, userId)}
                                      style={{ 
                                        padding: '4px 8px', 
                                        fontSize: '0.75rem',
                                        borderRadius: '4px',
                                        background: '#e2e8f0',
                                        color: '#2d3748',
                                        border: '1px solid #cbd5e0',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                      }}
                                      title={`Move to ${targetCarpool.title}`}
                                    >
                                      → {targetCarpool.title.substring(0, 10)}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => handleRemovePassenger(carpool.id, userId)}
                                    style={{ 
                                      padding: '4px 8px', 
                                      fontSize: '0.75rem',
                                      borderRadius: '4px',
                                      background: '#fed7d7',
                                      color: '#c53030',
                                      border: '1px solid #feb2b2',
                                      cursor: 'pointer',
                                      fontWeight: '600'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                              {!isAdmin && isCurrentUser && (
                                <button
                                  onClick={() => handleLeaveCarpool(carpool.id)}
                                  className="btn btn-secondary"
                                  style={{ 
                                    padding: '4px 8px', 
                                    fontSize: '0.75rem',
                                    borderRadius: '4px',
                                    background: '#fed7d7',
                                    color: '#c53030',
                                    border: '1px solid #feb2b2'
                                  }}
                                >
                                  Leave
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Group Chat Buttons */}
                  {(isUserDriver || isAdmin) && passengers.length > 0 && (
                    <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {isUserDriver && passengers.filter(userId => userId !== carpool.driverUserId).length > 0 && (
                        <button
                          onClick={() => handleCreateGroupChat(carpool)}
                          className="btn btn-primary"
                          style={{ 
                            width: '100%',
                            padding: '10px 16px',
                            fontSize: '0.95rem',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          💬 Create Group Chat with Passengers
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleCreateGroupChatForAdmin(carpool)}
                          className="btn btn-secondary"
                          style={{ 
                            width: '100%',
                            padding: '10px 16px',
                            fontSize: '0.95rem',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            background: '#e2e8f0',
                            color: '#2d3748',
                            border: '1px solid #cbd5e0'
                          }}
                        >
                          💬 Create Group Chat (All Passengers + Driver)
                        </button>
                      )}
                    </div>
                  )}

                  {/* Departure Info Section - Editable by Admin or Driver */}
                  {canEditDeparture && (
                    <div style={{ 
                      marginTop: '15px', 
                      padding: '12px', 
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <h5 style={{ margin: '0 0 10px 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        📅 Departure Details
                      </h5>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
                            Day
                          </label>
                          <input
                            type="text"
                            value={editingDeparture[`${carpool.id}_day`] !== undefined ? editingDeparture[`${carpool.id}_day`] : (carpool.departureDay || '')}
                            onChange={(e) => {
                              setEditingDeparture({
                                ...editingDeparture,
                                [`${carpool.id}_day`]: e.target.value
                              });
                            }}
                            onBlur={() => {
                              const day = editingDeparture[`${carpool.id}_day`] !== undefined ? editingDeparture[`${carpool.id}_day`] : carpool.departureDay;
                              const time = editingDeparture[`${carpool.id}_time`] !== undefined ? editingDeparture[`${carpool.id}_time`] : carpool.departureTime;
                              handleUpdateDeparture(carpool.id, day, time);
                              // Clear editing state
                              const newEditing = { ...editingDeparture };
                              delete newEditing[`${carpool.id}_day`];
                              delete newEditing[`${carpool.id}_time`];
                              setEditingDeparture(newEditing);
                            }}
                            placeholder="e.g., Thursday, Jan 15"
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)',
                              fontSize: '0.85rem',
                              background: 'var(--bg-primary)',
                              color: 'var(--text-primary)'
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
                            Time
                          </label>
                          <input
                            type="text"
                            value={editingDeparture[`${carpool.id}_time`] !== undefined ? editingDeparture[`${carpool.id}_time`] : (carpool.departureTime || '')}
                            onChange={(e) => {
                              setEditingDeparture({
                                ...editingDeparture,
                                [`${carpool.id}_time`]: e.target.value
                              });
                            }}
                            onBlur={() => {
                              const day = editingDeparture[`${carpool.id}_day`] !== undefined ? editingDeparture[`${carpool.id}_day`] : carpool.departureDay;
                              const time = editingDeparture[`${carpool.id}_time`] !== undefined ? editingDeparture[`${carpool.id}_time`] : carpool.departureTime;
                              handleUpdateDeparture(carpool.id, day, time);
                              // Clear editing state
                              const newEditing = { ...editingDeparture };
                              delete newEditing[`${carpool.id}_day`];
                              delete newEditing[`${carpool.id}_time`];
                              setEditingDeparture(newEditing);
                            }}
                            placeholder="e.g., 12:00 PM"
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)',
                              fontSize: '0.85rem',
                              background: 'var(--bg-primary)',
                              color: 'var(--text-primary)'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {!isAdmin && !isUserInCarpool && availableSpots > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <button
                        onClick={() => handleJoinCarpool(carpool.id)}
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                      >
                        Join This Carpool
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'var(--bg-primary)',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Create New Carpool</h2>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={newCarpool.title}
                  onChange={(e) => setNewCarpool({ ...newCarpool, title: e.target.value })}
                  placeholder="e.g., Car 1, SUV Group, etc."
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Driver *
                </label>
                <select
                  value={newCarpool.driverUserId}
                  onChange={(e) => {
                    const selectedUserId = e.target.value;
                    const selectedUser = responses.find(r => r.userId === selectedUserId);
                    setNewCarpool({ 
                      ...newCarpool, 
                      driverUserId: selectedUserId,
                      driver: selectedUser?.userName || ''
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="">Select a driver...</option>
                  {responses
                    .filter(r => {
                      // Only show confirmed users
                      if (r.status !== 'confirmed' && r.status) return false;
                      // Exclude current user from dropdown (they're auto-selected)
                      if (r.userId === currentUser?.uid) return false;
                      // Only show users who indicated they can drive
                      return r.answers?.transportation === 'drive';
                    })
                    .map(response => (
                      <option key={response.userId} value={response.userId}>
                        {response.userName} ({response.phoneNumber})
                      </option>
                    ))}
                </select>
                {currentUser && newCarpool.driverUserId === currentUser.uid && (
                  <p style={{ marginTop: '5px', fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    You are the driver (automatically set)
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Departure Day
                </label>
                <input
                  type="text"
                  value={newCarpool.departureDay}
                  onChange={(e) => setNewCarpool({ ...newCarpool, departureDay: e.target.value })}
                  placeholder="e.g., Thursday, Jan 15"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Departure Time
                </label>
                <input
                  type="text"
                  value={newCarpool.departureTime}
                  onChange={(e) => setNewCarpool({ ...newCarpool, departureTime: e.target.value })}
                  placeholder="e.g., 12:00 PM"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Number of Spots (including driver)
                </label>
                <input
                  type="number"
                  min="2"
                  max="15"
                  value={newCarpool.maxSpots}
                  onChange={(e) => setNewCarpool({ ...newCarpool, maxSpots: parseInt(e.target.value) || 4 })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewCarpool({ title: '', driverUserId: '', driver: '', maxSpots: 4, departureDay: '', departureTime: '' });
                    setError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCarpool}
                  className="btn btn-primary"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingCarpool && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'var(--bg-primary)',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Edit Carpool</h2>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={editingCarpool.title}
                  onChange={(e) => setEditingCarpool({ ...editingCarpool, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Driver *
                </label>
                <select
                  value={editingCarpool.driverUserId || ''}
                  onChange={(e) => {
                    const selectedUserId = e.target.value;
                    const selectedUser = responses.find(r => r.userId === selectedUserId);
                    setEditingCarpool({ 
                      ...editingCarpool, 
                      driverUserId: selectedUserId,
                      driver: selectedUser?.userName || ''
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="">Select a driver...</option>
                  {responses
                    .filter(r => {
                      // Only show confirmed users
                      if (r.status !== 'confirmed' && r.status) return false;
                      // Always show the current driver (in case they were set before filtering was added)
                      if (r.userId === editingCarpool.driverUserId) return true;
                      // Only show users who indicated they can drive
                      return r.answers?.transportation === 'drive';
                    })
                    .map(response => (
                      <option key={response.userId} value={response.userId}>
                        {response.userName} ({response.phoneNumber})
                      </option>
                    ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Departure Day
                </label>
                <input
                  type="text"
                  value={editingCarpool.departureDay || ''}
                  onChange={(e) => setEditingCarpool({ ...editingCarpool, departureDay: e.target.value })}
                  placeholder="e.g., Thursday, Jan 15"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Departure Time
                </label>
                <input
                  type="text"
                  value={editingCarpool.departureTime || ''}
                  onChange={(e) => setEditingCarpool({ ...editingCarpool, departureTime: e.target.value })}
                  placeholder="e.g., 12:00 PM"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Number of Spots (including driver)
                </label>
                <input
                  type="number"
                  min="2"
                  max="15"
                  value={editingCarpool.maxSpots}
                  onChange={(e) => setEditingCarpool({ ...editingCarpool, maxSpots: parseInt(e.target.value) || 4 })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
                {editingCarpool.passengers && editingCarpool.passengers.length > editingCarpool.maxSpots && (
                  <p style={{ color: '#c53030', fontSize: '0.85rem', marginTop: '5px' }}>
                    Warning: Reducing spots below current passengers will require removing some passengers.
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setEditingCarpool(null);
                    setError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateCarpool}
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Carpools;
